import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { ChevronLeft, Settings, Mail, Lock, Globe } from 'lucide-react'

interface UserSettingsProps {
  user: {
    id: string
    email: string
    name: string
    timezone: string
    pendingEmail: string | null
  }
  timezones: Array<{ name: string; display: string }>
}

export default function UserSettingsIndex({ user, timezones }: UserSettingsProps) {
  const { data, setData, patch, processing } = useForm({
    user: {
      name: user.name || '',
      timezone: user.timezone,
    }
  })

  const [emailChangeState, setEmailChangeState] = useState<'idle' | 'input'>('idle')
  const [newEmail, setNewEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null)
  const [emailSubmitting, setEmailSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch('/user_settings')
  }

  const handleInitiateEmailChange = async () => {
    setEmailError(null)
    setEmailSubmitting(true)
    try {
      const response = await fetch('/user_settings/initiate_email_change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          new_email: newEmail,
          current_password: emailPassword,
        }),
      })

      const result = await response.json()

      if (response.ok) {
        setEmailSuccess(result.message)
        setEmailChangeState('idle')
        setNewEmail('')
        setEmailPassword('')
        // Refresh the page to show pending email status
        router.reload()
      } else {
        setEmailError(result.error)
      }
    } catch (error) {
      setEmailError('An error occurred. Please try again.')
    } finally {
      setEmailSubmitting(false)
    }
  }

  const handleCancelEmailChange = () => {
    router.delete('/user_settings/cancel_email_change')
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-2xl mx-auto px-6 py-6">
          {/* Breadcrumb */}
          <button
            onClick={() => router.visit('/reports')}
            className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-6"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Reports</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center">
              <Settings className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                User Settings
              </h1>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Manage your profile and account preferences
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Section */}
        <section className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-stone-400" />
            <h2 className="text-sm font-medium text-stone-900 dark:text-stone-100">Profile</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                Display Name
              </label>
              <input
                type="text"
                value={data.user.name}
                onChange={(e) => setData('user', { ...data.user, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                <Globe className="w-3.5 h-3.5 inline mr-1" />
                Timezone
              </label>
              <select
                value={data.user.timezone}
                onChange={(e) => setData('user', { ...data.user, timezone: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                {timezones.map((tz) => (
                  <option key={tz.name} value={tz.name}>
                    {tz.display}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
                All times in the application will be displayed in this timezone.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={processing}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </section>

        {/* Email Section */}
        <section className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-4 h-4 text-stone-400" />
            <h2 className="text-sm font-medium text-stone-900 dark:text-stone-100">Email Address</h2>
          </div>

          <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
            Current email: <span className="font-medium text-stone-900 dark:text-stone-100">{user.email}</span>
          </p>

          {user.pendingEmail && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Pending email change to: <strong>{user.pendingEmail}</strong>
                <br />
                Please check your inbox to confirm.
              </p>
              <button
                onClick={handleCancelEmailChange}
                className="mt-2 text-sm text-amber-700 dark:text-amber-300 underline hover:no-underline"
              >
                Cancel email change
              </button>
            </div>
          )}

          {emailSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-300">{emailSuccess}</p>
            </div>
          )}

          {emailChangeState === 'idle' && !user.pendingEmail && (
            <button
              onClick={() => setEmailChangeState('input')}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
            >
              Change Email
            </button>
          )}

          {emailChangeState === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                  New Email Address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  placeholder="new@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
                  Current Password (required)
                </label>
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 dark:border-stone-700 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  placeholder="Enter your current password"
                />
              </div>
              {emailError && (
                <p className="text-sm text-red-600 dark:text-red-400">{emailError}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleInitiateEmailChange}
                  disabled={!newEmail || !emailPassword || emailSubmitting}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {emailSubmitting ? 'Sending...' : 'Send Confirmation'}
                </button>
                <button
                  onClick={() => {
                    setEmailChangeState('idle')
                    setNewEmail('')
                    setEmailPassword('')
                    setEmailError(null)
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Password Section */}
        <section className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-stone-400" />
            <h2 className="text-sm font-medium text-stone-900 dark:text-stone-100">Password</h2>
          </div>

          <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
            Update your password to keep your account secure.
          </p>

          <button
            onClick={() => router.visit('/user_settings/change_password')}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
          >
            Change Password
          </button>
        </section>
      </main>
    </div>
  )
}
